import { useEffect, useState } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  FormControlLabel, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../../services/api';
import type { EmballageFacility } from '../../types';

interface Props {
  onNotify: (msg: string, error?: boolean) => void;
  onChanged: () => void;
}

export default function EmballageFacilitiesPanel({ onNotify, onChanged }: Props) {
  const [facilities, setFacilities] = useState<EmballageFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<EmballageFacility> | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setFacilities((await api.getEmballageFacilities()).filter(f => f.active));
    } catch {
      onNotify('Modtageanlæggene kunne ikke hentes', true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const remove = async (f: EmballageFacility) => {
    if (!window.confirm(`Fjern ${f.name}? Tidligere rapporter beholder anlægget som modtager.`)) return;
    try {
      await api.deleteEmballageFacility(f.id);
      await load();
      onChanged();
    } catch {
      onNotify('Anlægget blev ikke fjernet', true);
    }
  };

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ active: true })}>
          Nyt modtageanlæg
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
      ) : facilities.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Intet modtageanlæg oprettet endnu.</Typography>
        </Card>
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Anlæg</TableCell>
                <TableCell>CVR</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell>Kontakt</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {facilities.map(f => (
                <TableRow key={f.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{f.name}</span>
                      {f.isDefault && <Chip size="small" label="Standard" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{f.cvr || ''}</TableCell>
                  <TableCell>{[f.address, [f.postcode, f.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</TableCell>
                  <TableCell>{[f.contactName, f.contactEmail].filter(Boolean).join(', ')}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setEditing(f)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => remove(f)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {editing && (
        <FacilityDialog
          facility={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); onChanged(); }}
          onNotify={onNotify}
        />
      )}
    </Box>
  );
}

function FacilityDialog({ facility, onClose, onSaved, onNotify }: {
  facility: Partial<EmballageFacility>;
  onClose: () => void;
  onSaved: () => void;
  onNotify: (msg: string, error?: boolean) => void;
}) {
  const [form, setForm] = useState<Partial<EmballageFacility>>(facility);
  const [saving, setSaving] = useState(false);
  const set = (patch: Partial<EmballageFacility>) => setForm(f => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.name?.trim()) {
      onNotify('Anlægget skal have et navn', true);
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      cvr: form.cvr?.trim() || null,
      address: form.address?.trim() || null,
      postcode: form.postcode?.trim() || null,
      city: form.city?.trim() || null,
      contactName: form.contactName?.trim() || null,
      contactEmail: form.contactEmail?.trim() || null,
      note: form.note?.trim() || null,
      isDefault: !!form.isDefault,
      active: true,
    };
    try {
      if (form.id) await api.updateEmballageFacility(form.id, payload);
      else await api.createEmballageFacility(payload);
      onSaved();
    } catch {
      onNotify('Anlægget blev ikke gemt', true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{form.id ? 'Rediger modtageanlæg' : 'Nyt modtageanlæg'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Navn" size="small" fullWidth autoFocus
            value={form.name ?? ''} onChange={e => set({ name: e.target.value })} />
          <TextField label="CVR" size="small" fullWidth
            value={form.cvr ?? ''} onChange={e => set({ cvr: e.target.value })} />
          <TextField label="Adresse" size="small" fullWidth
            value={form.address ?? ''} onChange={e => set({ address: e.target.value })} />
          <Stack direction="row" spacing={2}>
            <TextField label="Postnummer" size="small" sx={{ width: 140 }}
              value={form.postcode ?? ''} onChange={e => set({ postcode: e.target.value })} />
            <TextField label="By" size="small" fullWidth
              value={form.city ?? ''} onChange={e => set({ city: e.target.value })} />
          </Stack>
          <TextField label="Kontaktperson" size="small" fullWidth
            value={form.contactName ?? ''} onChange={e => set({ contactName: e.target.value })} />
          <TextField label="E-mail" size="small" fullWidth
            value={form.contactEmail ?? ''} onChange={e => set({ contactEmail: e.target.value })} />
          <FormControlLabel
            control={<Switch checked={!!form.isDefault} onChange={e => set({ isDefault: e.target.checked })} />}
            label="Standardanlæg"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annullér</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Gem</Button>
      </DialogActions>
    </Dialog>
  );
}
